/**
 * @module placeholderPlugin
 * @description Custom Milkdown plugin for resolving {{PLACEHOLDER}} tokens
 *
 * In WYSIWYG mode, placeholders like {{TITLE}}, {{AUTHOR}}, {{PLACE}} are
 * resolved to their values from the project metadata and displayed as
 * styled badges. In source/markdown mode, the raw {{PLACEHOLDER}} text
 * is preserved.
 *
 * Double-clicking a badge opens an inline input to edit the metadata value
 * directly without navigating to the config panel.
 *
 * Uses ProseMirror decorations (Decoration.inline + Decoration.widget).
 * Delegates parsing and resolution to placeholderService.ts.
 */

import { Plugin, PluginKey } from "@milkdown/kit/prose/state";
import { Decoration, DecorationSet } from "@milkdown/kit/prose/view";
import { $prose } from "@milkdown/kit/utils";
import { selectedProject, appActions } from "../stores/appStore";
import { get } from "svelte/store";
import {
  parseProjectMetadata,
  getPlaceholderMap,
  resolvePlaceholder,
  updateYamlField,
  type ParsedMetadata,
} from "../services/placeholderService";

// ============================================================================
// INLINE EDITING
// ============================================================================

/**
 * Save an edited placeholder value back to the project YAML metadata.
 */
async function saveInlineEdit(placeholderName: string, newValue: string): Promise<boolean> {
  const project = get(selectedProject);
  if (!project) return false;

  const updatedYaml = updateYamlField(project.metadata, placeholderName, newValue);
  if (updatedYaml === null) return false;

  try {
    await appActions.updateProject(project.id, { metadata: updatedYaml });
    return true;
  } catch (e) {
    console.error('Failed to save placeholder edit:', e);
    return false;
  }
}

/**
 * Replace a badge element with an inline text input for editing.
 */
function openInlineEditor(badge: HTMLElement, name: string, currentValue: string): void {
  // Prevent opening multiple editors
  if (badge.querySelector('.placeholder-inline-input')) return;

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'placeholder-inline-input';
  input.value = currentValue;
  input.placeholder = name.replace(/_/g, ' ');

  // Size input to content
  input.style.width = Math.max(120, Math.min(400, currentValue.length * 8 + 40)) + 'px';

  // Replace badge content with input
  const originalText = badge.textContent;
  const originalClasses = [...badge.classList];
  badge.textContent = '';
  badge.appendChild(input);
  badge.classList.add('placeholder-editing');

  input.focus();
  input.select();

  let saved = false;

  const commit = async () => {
    if (saved) return;
    saved = true;

    const newValue = input.value.trim();
    const changed = newValue !== currentValue;

    // Restore badge appearance
    badge.classList.remove('placeholder-editing');

    if (changed && newValue) {
      const success = await saveInlineEdit(name, newValue);
      if (success) {
        // Badge will be rebuilt by store subscription → decoration rebuild
        badge.textContent = newValue;
        badge.title = `{{${name}}} → ${newValue}`;
        return;
      }
    }

    // Restore original state
    badge.textContent = originalText;
    badge.className = originalClasses.join(' ');
    badge.title = currentValue
      ? `{{${name}}} → ${currentValue}`
      : `{{${name}}} (non defini)`;
  };

  input.addEventListener('blur', commit);
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      input.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      saved = true;
      // Restore without saving
      badge.classList.remove('placeholder-editing');
      badge.textContent = originalText;
      badge.className = originalClasses.join(' ');
    }
  });
}

// ============================================================================
// DECORATION BUILDING
// ============================================================================

const placeholderPluginKey = new PluginKey("placeholder-plugin");
const PLACEHOLDER_MAP = getPlaceholderMap();

// Regex to match {{PLACEHOLDER}} patterns
const PLACEHOLDER_REGEX = /\{\{([A-Z_]+)\}\}/g;

/**
 * Create a badge DOM element for a resolved placeholder
 */
function createPlaceholderBadge(name: string, value: string): HTMLElement {
  const badge = document.createElement("span");
  badge.classList.add("placeholder-badge");
  badge.contentEditable = "false";

  if (value) {
    badge.classList.add("placeholder-resolved");
    const firstLine = value.split('\n')[0];
    const display = firstLine.length > 60 ? firstLine.slice(0, 57) + '...' : firstLine;
    badge.textContent = display;
    badge.title = `{{${name}}} → ${value}`;
  } else {
    badge.classList.add("placeholder-empty");
    badge.textContent = name.replace(/_/g, ' ');
    badge.title = `{{${name}}} (non defini)`;
  }

  // Double-click to edit inline
  badge.addEventListener('dblclick', (e) => {
    e.preventDefault();
    e.stopPropagation();
    openInlineEditor(badge, name, value);
  });

  return badge;
}

/**
 * Build decorations for all {{PLACEHOLDER}} tokens in the document
 */
function buildDecorations(doc: any, metadata: ParsedMetadata): DecorationSet {
  const decorations: Decoration[] = [];

  doc.descendants((node: any, pos: number) => {
    if (!node.isText) return;

    const text = node.text as string;
    let match;
    PLACEHOLDER_REGEX.lastIndex = 0;

    while ((match = PLACEHOLDER_REGEX.exec(text)) !== null) {
      const placeholderName = match[1];
      // Accept built-in placeholders AND custom fields
      const resolvedValue = resolvePlaceholder(placeholderName, metadata);
      const isKnown = !!PLACEHOLDER_MAP[placeholderName] || resolvedValue !== '';
      if (!isKnown) continue;

      const from = pos + match.index;
      const to = from + match[0].length;

      // Hide the raw {{PLACEHOLDER}} text
      decorations.push(
        Decoration.inline(from, to, {
          class: "placeholder-hidden",
        })
      );

      // Insert badge widget at the start of the placeholder
      decorations.push(
        Decoration.widget(from, () => createPlaceholderBadge(placeholderName, resolvedValue), {
          side: -1,
          key: `placeholder-${from}`,
        })
      );
    }
  });

  return DecorationSet.create(doc, decorations);
}

// ============================================================================
// PLUGIN
// ============================================================================

/**
 * ProseMirror plugin that replaces {{PLACEHOLDER}} tokens with visual badges
 */
export const placeholderPlugin = $prose(() => {
  let currentMetadata: ParsedMetadata = parseProjectMetadata(
    get(selectedProject)?.metadata
  );
  let editorView: any = null;

  const unsubscribe = selectedProject.subscribe((project) => {
    currentMetadata = parseProjectMetadata(project?.metadata);
    if (editorView) {
      const tr = editorView.state.tr.setMeta(placeholderPluginKey, { metadataChanged: true });
      editorView.dispatch(tr);
    }
  });

  return new Plugin({
    key: placeholderPluginKey,
    state: {
      init(_, { doc }) {
        return buildDecorations(doc, currentMetadata);
      },
      apply(tr, oldDecorations) {
        if (tr.docChanged || tr.getMeta(placeholderPluginKey)) {
          return buildDecorations(tr.doc, currentMetadata);
        }
        return oldDecorations.map(tr.mapping, tr.doc);
      },
    },
    view(view) {
      editorView = view;
      return {
        destroy() {
          editorView = null;
          unsubscribe();
        },
      };
    },
    props: {
      decorations(state) {
        return placeholderPluginKey.getState(state) as DecorationSet;
      },
    },
  });
});

export default placeholderPlugin;
